import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatFAB from './components/ChatFAB';
import ChatModal from './components/ChatModal';
import { useCommunicationChat } from './hooks/useCommunicationChat';
import './styles/chat.css';

/**
 * ChatWidget — standalone floating chat widget.
 * Rendered once in App.js for logged-in users.
 * Uses v-ch- prefix for total isolation.
 */
const ChatWidget = ({ user }) => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const { rfpId, sectionId, isChatPath } = React.useMemo(() => {
        // Matches /rfp/:rfpId/sections/:sectionId/chat
        const chatMatch = location.pathname.match(/^\/rfp\/([^/]+)(?:\/sections\/([^/]+))?\/chat/);
        if (chatMatch) {
            return {
                rfpId: chatMatch[1],
                sectionId: chatMatch[2] !== 'None' ? chatMatch[2] : null,
                isChatPath: true
            };
        }

        const analysisMatch = location.pathname.match(/^\/proposals\/analysis-result\/([^/]+)/);
        if (analysisMatch?.[1]) {
            return { rfpId: analysisMatch[1], sectionId: null, isChatPath: false };
        }

        const pptMatch = location.pathname.match(/^\/proposals\/ppt-customization\/([^/]+)/);
        if (pptMatch?.[1]) {
            return { rfpId: pptMatch[1], sectionId: null, isChatPath: false };
        }

        return { rfpId: null, sectionId: null, isChatPath: false };
    }, [location.pathname]);

    const [activeTab, setActiveTab] = useState('ai');

    const chatController = useCommunicationChat({
        rfpId,
        currentUser: user,
        isModalOpen: isOpen,
        isTeamTabActive: isOpen && activeTab === 'team',
        skipAutoSelectFirst: isChatPath,
    });

    const prevPathRef = React.useRef('');

    React.useEffect(() => {
        if (isChatPath && location.pathname !== prevPathRef.current) {
            prevPathRef.current = location.pathname;
            setIsOpen(true);
            setActiveTab('team');

            const payload = sectionId
                ? { scope_type: 'RFP_SECTION', section_id: sectionId }
                : { scope_type: 'RFP_GENERAL' };

            if (chatController.resolveThread) {
                chatController.resolveThread(payload);
            }
        }
    }, [location.pathname, isChatPath, sectionId, chatController.resolveThread]);

    // Determine role for accent-color variant
    const designation = (user?.designation || '').toLowerCase();
    const userRole = (user?.role || '').toLowerCase();
    const isManager =
        designation.includes('lead') ||
        designation.includes('manager') ||
        userRole === 'admin' ||
        userRole === 'team_lead';

    const roleClass = isManager ? 'vchat--lead' : 'vchat--member';

    return (
        <div className={`v-ch-root ${roleClass} ${isOpen ? 'is-open' : ''}`}>
            {/* Modal shown above the FAB position */}
            {isOpen && (
                <ChatModal
                    onClose={() => {
                        setIsOpen(false);
                        if (isChatPath) {
                            navigate('/proposals');
                        }
                    }}
                    chatController={chatController}
                    rfpId={rfpId}
                    proposalTitle="Team Discussion"
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
            )}

            {/* FAB only shown when modal is closed */}
            {!isOpen && (
                <ChatFAB
                    isOpen={isOpen}
                    onClick={() => setIsOpen(true)}
                    unreadCount={chatController.unreadCount}
                />
            )}
        </div>
    );
};

export default ChatWidget;
